import { useMemo } from 'react';
import { reorder } from '../../_events/_manage-event/_edit-website/website-form-body/helpers';

export type UseNumberedHeightsResult = {
    onItemRendered: (element: Element, index: number) => () => void;
    updateHeights: (sourceIndex?: number, destinationIndex?: number) => void;
    registerNumberRef: (index: number) => (ref: HTMLSpanElement) => void;
};

export const selectReorderedHeights = (heights: string[], sourceIndex?: number, destinationIndex?: number): string[] => {
    if (typeof sourceIndex === 'number' && typeof destinationIndex === 'number') {
        return reorder<string>(heights, sourceIndex, destinationIndex);
    }

    if (typeof sourceIndex === 'number') {
        return heights.filter((item, index) => index !== sourceIndex);
    }

    return heights;
};

export const useNumberedHeights = (enabled: boolean): UseNumberedHeightsResult => {
    const numberRefs: HTMLSpanElement[] = useMemo(() => [], []);
    const draggableItemsHeights: string[] = useMemo(() => [], []);

    const setupResizeObserver = (element: Element, index: number): ResizeObserver | null => {
        if (element) {
            const resizeObserver = new ResizeObserver(entries => {
                if (numberRefs[index] && entries[0].target.clientHeight) {
                    numberRefs[index].style.height = entries[0].target.clientHeight + 'px';
                    draggableItemsHeights[index] = entries[0].target.clientHeight + 'px';
                }
            });
            resizeObserver.observe(element);
            return resizeObserver;
        }

        return null;
    };

    const onItemRendered = (element: Element, index: number): () => void => {
        const resizeOserver = setupResizeObserver(element, index);
        draggableItemsHeights[index] = element.clientHeight + 'px';

        return () => {
            if (resizeOserver) {
                resizeOserver.disconnect();
            }
            draggableItemsHeights.splice(index, 1);
        };
    };

    const updateHeights = (sourceIndex?: number, destinationIndex?: number): void => {
        if (!enabled) {
            return;
        }

        const reorderedItemsList = selectReorderedHeights(draggableItemsHeights, sourceIndex, destinationIndex);

        numberRefs.forEach((item, index) => {
            if (reorderedItemsList[index]) {
                item.style.height = reorderedItemsList[index];
            }
        });
    };

    const registerNumberRef = (index: number) => (ref: HTMLSpanElement): void => {
        numberRefs[index] = ref;
    };

    return { onItemRendered: onItemRendered, updateHeights: updateHeights, registerNumberRef: registerNumberRef };
};
